import { useEffect, useMemo, useRef, useState } from "react";
import "./index.css";
import { api } from "../../api/client";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage, auth } from "../../firebase";
import { v4 as uuid } from "uuid";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Loading from "../Loading";
import logo from "../../assets/originelogo.png";
import Mockup from "../Mockup";
import Footer from "../../components/Footer";

type Accord = {
  phase: "top" | "heart" | "base";
  ingredient: string;
  amount?: number;
  drops?: number;
  percent?: number;
};
type Formula = {
  details?: string;
  style?: string;
  accords?: Accord[];
  // любые прочие поля
  [k: string]: any;
};

type Parfume = {
  id: string;
  userId: string;
  name: string;
  img?: string;
  description?: string;
  question?: string;
  formula?: Formula | null;
};

type User = {
  id: string;
  createdAt: number;
  email: string;
  name: string;
  phone?: string;
  address?: string;
  status: string;
  paid: number;
  templateId?: string;
};

const StatusLabel: Record<string, string> = {
  draft: "В процессе",
  formula_production: "Формулы создаются",
  in_client: "Ожидание одобрения клиента",
  approved: "Одобрено клиентом",
  in_production: "В производстве",
  in_pack: "Упаковывается",
  shipped: "Отправлено",
  delivered: "Доставлено",
};

const StatusNote: Record<string, string> = {
  draft:
    "Этап заполнения. Добавьте 5 ассоциаций - и мы начнём создавать формулы для ваших парфюмов.",
  formula_production:
    "Формулы разрабатываются на основе ваших ассоциаций. После — мы покажем варианты на подтверждение.",
  in_client:
    "Формулы готовы к подтверждению. Утвердите их или попросите корректировки — затем запустим производство.",
  approved:
    "Вы удтвердили формулы. Совсем скоро мы приступим собирать парфюмы.",
  in_production:
    "Идёт производство. Готовим партии по утверждённым формулам и передаём на розлив и упаковку.",
  in_pack: "Заказ в процессе упаковки. Готовим к отправке по указанному адресу.",
  shipped:
    "Заказ отправлен. Для уточнения статуса доставки свяжитесь с нашим менеджером.",
  delivered: "Заказ доставлен. Спасибо — нам было приятно с вами работать!",
};

function byPhaseOrder(a: Accord, b: Accord) {
  const order: Record<Accord["phase"], number> = { top: 0, heart: 1, base: 2 };
  return (order[a.phase] ?? 99) - (order[b.phase] ?? 99);
}

function FormulaBlock({ formula }: { formula: Formula }) {
  const [open, setOpen] = useState(false);
  const accords = (formula?.accords || []).slice().sort(byPhaseOrder);

  return (
    <div className={`formula ${open ? "formula--open" : ""}`}>
      <button
        type="button"
        className="formula__toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="formula__dot" />
        <span className="formula__label">Формула готова</span>
        <span className="formula__chev" />
      </button>

      {open && (
        <div className="formula__body">
          {formula?.style && (
            <div className="formula__row">
              <span className="formula__key">Стиль</span>
              <span className="formula__val">{formula.style}</span>
            </div>
          )}

          {formula?.details && (
          <div className="formula__row">
              <span className="formula__key">От парфюмера</span>
              <span className="formula__val">{formula.details}</span>
            </div>
          )}

          {accords.length > 0 && (
            <div className="formula__list">
              {accords.map((a, i) => (
                <div key={i} className={`acc acc--${a.phase}`}>
                  <span className="acc__phase">
                    {a.phase === "top"
                      ? "Верх"
                      : a.phase === "heart"
                      ? "Сердце"
                      : "База"}
                  </span>
                  <span className="acc__name">{a.ingredient}</span>
                  {typeof a.drops === "number" && (
                    <span className="acc__qty">{a.drops} кап.</span>
                  )}
                  {!a.drops && typeof a.amount === "number" && (
                    <span className="acc__qty">{a.amount} мл</span>
                  )}
                  {!a.drops && !a.amount && typeof a.percent === "number" && (
                    <span className="acc__qty">{a.percent}%</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const nav = useNavigate();

  const [me, setMe] = useState<User | null>(null);
  const [parfumes, setParfumes] = useState<Parfume[]>([]);
  const [loading, setLoading] = useState(true);
  const [finishBusy, setFinishBusy] = useState(false);
  const [err, setErr] = useState("");

  // поля добавления (сейчас не используются на этой странице, оставлены про запас)
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const canEdit = useMemo(() => me?.status === "draft", [me?.status]);
  const reachedLimit = useMemo(() => parfumes.length >= 5, [parfumes]);

  async function fetchProfile() {
    setLoading(true);
    setErr("");
    try {
      const u = await api<{ ok: true; user: User }>("/client/me", {
        method: "GET",
      });
      const p = await api<{ ok: true; parfumes: Parfume[] }>(
        "/client/parfumes",
        { method: "GET" }
      );
      setMe(u.user);
      setParfumes(p.parfumes);
    } catch (e: any) {
      // авто-выход при битом токене
      if (e?.message === '{"ok":false,"code":"BAD_TOKEN"}') {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        nav("/login", { replace: true });
      }
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProfile();
  }, []);

  async function uploadImageIfAny(): Promise<string> {
    if (!file) return "";
    const uid = auth.currentUser?.uid || "anon";
    const path = `users/${uid}/parfumes/${uuid()}-${file.name}`;
    const r = ref(storage, path);
    await uploadBytes(r, file);
    return await getDownloadURL(r);
  }

  // зарезервировано (форма перенесена на отдельный экран Create)
  async function addParfume() {
    if (!name || !canEdit || reachedLimit) return;
    try {
      setBusy(true);
      const imgUrl = await uploadImageIfAny();

      await api<{ ok: true; parfume: Parfume }>("/client/create-parfume", {
        method: "POST",
        body: JSON.stringify({
          name,
          img: imgUrl,
          description: desc || "",
          question: "",
        }),
      });

      setName("");
      setDesc("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchProfile();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function finishAndGenerate() {
    if (!canEdit) return;
    try {
      setFinishBusy(true);
      await api<{ ok: true }>("/client/finish", { method: "POST" });
      await fetchProfile();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setFinishBusy(false);
    }
  }

  async function logout() {
    try {
      await signOut(auth);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      nav("/login", { replace: true });
    }
  }

  if (loading || finishBusy) return <Loading />;

  if (err)
    return (
      <div className="cabinet" style={{ color: "crimson", fontWeight: 700 }}>
        {err}
      </div>
    );

  const status = me?.status || "draft";

  return (
    <div className="cabinet">
      <header className="cabinet__top">
        <img src={logo} className="login__logo" />
        <button className="pill pill--ghost" onClick={logout}>
          Выйти
        </button>
      </header>

      <main className="cabinet__main">
        {/* hero */}
        <section className="hero">
          <h1 className="hero__title">{me?.name}</h1>
          <div className="hero__email">{me?.email}</div>
          <div className="hero__status">
            <span className="badge">{StatusLabel[status] || status}</span>
          </div>
          <p className="hero__note">{StatusNote[status]}</p>

          {status === "draft" && parfumes.length < 5 && (
            <div
              onClick={() => nav("/create")}
              className="profile-button"
              role="button"
              tabIndex={0}
            >
              Добавить парфюм ({parfumes.length} из 5)
            </div>
          )}

          {status === "draft" && parfumes.length >= 5 && (
            <div
              onClick={finishAndGenerate}
              className="profile-button"
              role="button"
              tabIndex={0}
            >
              Завершить
            </div>
          )}
        </section>

        {/* grid */}
        <section className="grid">
          {parfumes.map((p) => (
            <article
              key={p.id}
              className="card"
              onClick={() => nav(`/edit/${p.id}`)}
            >
              <div
                className="card__image"
                style={
                  p.img
                    ? {
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }
                    : undefined
                }
              >
                <Mockup title={p.name || ""} photoUrl={p.img || ""} />
              </div>
              
              <h3 className="card__title">{p.name}</h3>
              {p.description && <p className="card__desc">{p.description}</p>}

              {p.formula && status === "in_client" && (
                <div
                  className="card__formula"
                  // остановим всплытие, чтобы клик по раскрывашке не вёл в /edit
                  onClick={(e) => e.stopPropagation()}
                >
                  <FormulaBlock formula={p.formula} />
                </div>
              )}
            </article>
          ))}
        </section>

        {/* <section className="hero">
          <h1 className="hero__title">Так будет выглядеть упаковка</h1>
        </section> */}

        <Footer />
      </main>
    </div>
  );
}
